import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {Button} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {Message} from 'src/components';
import {Text} from 'src/components/primitives';
import {CardIcon, TrashIcon} from 'src/icons';
import {parseISO} from 'src/utils';

import {useGetPayKeeperInfoQuery} from '../api';
import * as S from '../style';
import {showPayKeeperDeleteCardModal} from './PayKeeperDeleteCardModal';
import {showPayKeeperOnetimePaymentSettingsModal} from './PayKeeperSettingsModal';
import {showStripeOnetimePaymentSettingsModal} from './StripeSettingsModal';

const PayKeeperOnetimePaymentInfo = React.memo(() => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();
  const {data: payKeeperInfo} = useGetPayKeeperInfoQuery(undefined, {skip: !!supportTenantId});

  if (supportTenantId || !payKeeperInfo?.maskedCardNumber) {
    return null;
  }

  return (
    <>
      <Stack size="2">
        <Stack direction="ROW" align="center" size="2">
          <Stack direction="ROW" align="center" size="1">
            <Icon as={CardIcon} size="20px" color="grey400" />
            <Text weight={500}>{t('BalanceReplenishment:status.savedCard')}</Text>
          </Stack>
          <Button onClick={showPayKeeperDeleteCardModal} size="XS">
            <S.ButtonLeftIcon as={TrashIcon} />
            {t('BalanceReplenishment:actions.deleteCard')}
          </Button>
        </Stack>
        <S.NestedBlock>
          <Text color="grey400" size="XS">
            <Stack direction="ROW" align="center" size="2">
              <Icon as={CardIcon} size="16px" />
              {payKeeperInfo.maskedCardNumber}
            </Stack>
          </Text>
        </S.NestedBlock>
      </Stack>
      {!!payKeeperInfo.errorDate && (
        <S.NestedBlock>
          <Message type="Error" shadowSize="XS" fontSize="S" noMargin closible>
            {t('BalanceReplenishment:errors.onetimePayment', {
              date: parseISO(payKeeperInfo.errorDate, 'dd.MM.yyyy'),
            })}
          </Message>
        </S.NestedBlock>
      )}
    </>
  );
});

const PayKeeperOnetimePaymentButton = React.memo(() => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  if (supportTenantId) {
    return null;
  }

  return (
    <Button onClick={showPayKeeperOnetimePaymentSettingsModal} size="S">
      <S.ButtonLeftIcon as={CardIcon} />
      {t('BalanceReplenishment:actions.payByCard')}
    </Button>
  );
});

const StripeOnetimePaymentButton = React.memo(() => {
  const {t} = useTranslation();

  return (
    <Button onClick={showStripeOnetimePaymentSettingsModal} size="S">
      <S.ButtonLeftIcon as={CardIcon} />
      {t('BalanceReplenishment:actions.payByCard')}
    </Button>
  );
});

export {PayKeeperOnetimePaymentInfo, PayKeeperOnetimePaymentButton, StripeOnetimePaymentButton};
